import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";
import { SITE, SITE_URL } from "@/lib/site";
import { useContactInfo } from "@/components/site/ContactInfoProvider";

const DEFAULT_TITLE = "Política de Privacidad · Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Conoce cómo Improve Yourself trata y protege tus datos personales conforme a la Ley N° 29733 — Ley de Protección de Datos Personales del Perú.";

export const Route = createFileRoute("/politica-de-privacidad")({
  component: PoliticaDePrivacidad,
  head: ({ match }) => {
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/politica-de-privacidad",
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/politica-de-privacidad"),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": absoluteUrl("/politica-de-privacidad"),
            url: absoluteUrl("/politica-de-privacidad"),
            name: "Política de Privacidad · Improve Yourself",
            inLanguage: "es-PE",
            datePublished: "2026-06-02",
            description:
              "Política de privacidad y tratamiento de datos personales de Improve Yourself, conforme a la Ley N° 29733 del Perú.",
            publisher: {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: SITE.name,
              url: SITE_URL,
            },
          }),
        },
      ],
    };
  },
});

function PoliticaDePrivacidad() {
  const contact = useContactInfo();
  return (
    <PageShell>
      <PageHero
        eyebrow="· Legal"
        h1="Política de Privacidad"
        intro="Cómo Improve Yourself recopila, usa y protege tus datos personales, conforme a la Ley N° 29733."
      />

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <article className="space-y-10 text-secondary/80">
            <p className="text-sm text-secondary/50">Última actualización: 2 de junio de 2026</p>

            {/* 1. Responsable */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                1. Responsable del tratamiento
              </h2>
              <p className="mt-4 leading-relaxed">
                El responsable del tratamiento de tus datos personales es{" "}
                <strong>Improve Yourself</strong> (RUC {SITE.ruc}), con domicilio en la ciudad de{" "}
                {SITE.city}, {SITE.country}. Puedes contactarnos en{" "}
                <a
                  href={`mailto:${contact.email}`}
                  className="font-medium text-primary hover:underline"
                >
                  {contact.email}
                </a>{" "}
                o al teléfono {contact.phone}.
              </p>
            </div>

            {/* 2. Datos que recopilamos */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                2. Datos personales que recopilamos
              </h2>
              <p className="mt-4 leading-relaxed">
                A través del formulario de cotización de nuestro sitio web, recopilamos los
                siguientes datos personales:
              </p>
              <ul className="mt-3 list-inside list-disc space-y-1 pl-2 leading-relaxed">
                <li>Nombre y apellido</li>
                <li>Nombre de la empresa o marca</li>
                <li>Número de teléfono o WhatsApp</li>
                <li>Dirección de correo electrónico</li>
                <li>Tipo de servicio de interés</li>
                <li>Descripción del proyecto (opcional)</li>
                <li>Página del sitio desde la que se envió la solicitud</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                No recopilamos datos sensibles (salud, origen racial, afiliación política, creencias
                religiosas, etc.) ni datos de menores de edad.
              </p>
            </div>

            {/* 3. Finalidades */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                3. Finalidades del tratamiento
              </h2>
              <p className="mt-4 leading-relaxed">
                Tratamos tus datos personales con las siguientes finalidades:
              </p>
              <ul className="mt-3 list-inside list-disc space-y-1 pl-2 leading-relaxed">
                <li>
                  <strong>Atender tu solicitud de cotización</strong>: responder a tu consulta y
                  enviarte una propuesta comercial a medida.
                </li>
                <li>
                  <strong>Comunicaciones comerciales relacionadas</strong>: informarte sobre
                  servicios, promociones o novedades de Improve Yourself que puedan ser de tu
                  interés, siempre que hayas prestado consentimiento.
                </li>
              </ul>
              <p className="mt-4 leading-relaxed">
                No utilizamos tus datos para fines distintos a los indicados sin tu consentimiento
                previo.
              </p>
            </div>

            {/* 4. Base legal */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                4. Base legal del tratamiento
              </h2>
              <p className="mt-4 leading-relaxed">
                El tratamiento de tus datos personales se basa en tu{" "}
                <strong>consentimiento expreso</strong>, conforme al artículo 13 de la Ley N° 29733
                — Ley de Protección de Datos Personales del Perú. Al marcar la casilla de aceptación
                en el formulario, consientes libre y voluntariamente el tratamiento de tus datos
                para las finalidades indicadas en la sección anterior.
              </p>
              <p className="mt-4 leading-relaxed">
                Puedes revocar tu consentimiento en cualquier momento escribiéndonos a{" "}
                <a
                  href={`mailto:${contact.email}`}
                  className="font-medium text-primary hover:underline"
                >
                  {contact.email}
                </a>
                . La revocación no afecta la licitud del tratamiento basado en el consentimiento
                previo a su retirada.
              </p>
            </div>

            {/* 5. Conservación */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                5. Conservación de los datos
              </h2>
              <p className="mt-4 leading-relaxed">
                Conservamos tus datos personales mientras exista una relación comercial activa o
                mientras no solicites su cancelación. Una vez concluida la relación comercial, los
                datos podrán conservarse durante el plazo necesario para el cumplimiento de
                obligaciones legales o para la defensa de reclamaciones.
              </p>
            </div>

            {/* 6. Derechos ARCO */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                6. Tus derechos (ARCO)
              </h2>
              <p className="mt-4 leading-relaxed">
                Conforme a la Ley N° 29733 y su Reglamento (D.S. N° 003-2013-JUS), tienes derecho a:
              </p>
              <ul className="mt-3 list-inside list-disc space-y-1 pl-2 leading-relaxed">
                <li>
                  <strong>Acceso</strong>: conocer qué datos personales tuyos tratamos.
                </li>
                <li>
                  <strong>Rectificación</strong>: solicitar la corrección de datos inexactos o
                  incompletos.
                </li>
                <li>
                  <strong>Cancelación</strong>: solicitar la supresión de tus datos cuando ya no
                  sean necesarios para la finalidad con la que fueron recogidos.
                </li>
                <li>
                  <strong>Oposición</strong>: oponerte al tratamiento de tus datos en determinadas
                  circunstancias.
                </li>
              </ul>
              <p className="mt-4 leading-relaxed">
                Para ejercer cualquiera de estos derechos, envía un correo a{" "}
                <a
                  href={`mailto:${contact.email}`}
                  className="font-medium text-primary hover:underline"
                >
                  {contact.email}
                </a>{" "}
                indicando tu nombre completo, el derecho que deseas ejercer y una copia de tu
                documento de identidad. Confirmaremos la recepción de tu solicitud en un plazo de{" "}
                <strong>10 días hábiles</strong> y la resolveremos en un plazo de{" "}
                <strong>20 días hábiles</strong>, conforme a lo establecido en la ley.
              </p>
            </div>

            {/* 7. Seguridad */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                7. Seguridad de los datos
              </h2>
              <p className="mt-4 leading-relaxed">
                Aplicamos medidas técnicas y organizativas adecuadas para proteger tus datos
                personales frente a acceso no autorizado, pérdida, destrucción o divulgación
                accidental. Los datos son almacenados en servidores con controles de acceso
                restringidos y cifrado en tránsito.
              </p>
              <p className="mt-4 leading-relaxed">
                No transferimos tus datos personales a terceros, salvo obligación legal o cuando sea
                estrictamente necesario para prestarte el servicio solicitado (por ejemplo,
                proveedores de infraestructura tecnológica que actúan como encargados del
                tratamiento bajo acuerdos de confidencialidad). No realizamos transferencias
                internacionales de datos.
              </p>
            </div>

            {/* 8. Marco legal */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                8. Marco legal aplicable
              </h2>
              <p className="mt-4 leading-relaxed">
                Esta política se rige por la legislación peruana, en particular:
              </p>
              <ul className="mt-3 list-inside list-disc space-y-1 pl-2 leading-relaxed">
                <li>
                  <strong>Ley N° 29733</strong> — Ley de Protección de Datos Personales del Perú.
                </li>
                <li>
                  <strong>D.S. N° 003-2013-JUS</strong> — Reglamento de la Ley de Protección de
                  Datos Personales.
                </li>
              </ul>
              <p className="mt-4 leading-relaxed">
                La autoridad competente en materia de protección de datos personales es la Autoridad
                Nacional de Protección de Datos Personales (ANPDP), dependiente del Ministerio de
                Justicia y Derechos Humanos del Perú.
              </p>
            </div>

            {/* 9. Vigencia */}
            <div>
              <h2 className="font-display text-2xl font-bold text-secondary">
                9. Vigencia y modificaciones
              </h2>
              <p className="mt-4 leading-relaxed">
                Esta política entra en vigor el 2 de junio de 2026. Nos reservamos el derecho de
                actualizarla para adaptarla a cambios normativos o en nuestras prácticas de
                tratamiento. Cualquier modificación relevante será publicada en esta misma página
                con indicación de la fecha de actualización.
              </p>
              <p className="mt-4 leading-relaxed">
                Si tienes alguna consulta sobre esta política, escríbenos a{" "}
                <a
                  href={`mailto:${contact.email}`}
                  className="font-medium text-primary hover:underline"
                >
                  {contact.email}
                </a>
                .
              </p>
            </div>
          </article>
        </div>
      </section>
    </PageShell>
  );
}
